<template>
	<span class="label-container" :class="sizeClass">
		<label :for="inputId || undefined">
			<div class="inline-flex gap-2 items-center mr-2" v-tooltip.top="labelTooltipValue">
				<slot>{{ label }}</slot>
				<span v-if="helpElementName && labelHasHelpText" class="info-icon" @click.stop="helpEditor.openHelpViewer(helpElementName, label)">
					<FontAwesomeIcon :icon="['far', 'circle-info']" />
				</span>
				<span v-if="isAdminUser && helpElementName" class="pencil-icon" @click.stop="onPencilClick">
					<FontAwesomeIcon :icon="['far', 'pen']" />
				</span>
			</div>
		</label>

		<ContextMenu v-if="isAdminUser && helpElementName" ref="contextMenu" :model="contextMenuItems" />
	</span>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import ContextMenu from "primevue/contextmenu"
import { useHelpLabelStore } from "@/store/helpLabelStore"
import { useHelpEditor } from "@/composables/useHelpEditor"
import { SessionInfo } from "@/auth/personSessionData"

const props = defineProps({
	label: {
		type: String,
		required: true,
	},
	helpElementName: {
		type: String,
		required: false,
		default: "",
	},
	size: {
		type: String as () => "h1" | "h2" | "h3" | "h4" | "normal",
		required: false,
		default: "normal",
	},
	// Optional id of the input this label describes, so clicking the label text
	// activates the control (e.g. ToggleSwitch/Checkbox with a matching input-id).
	inputId: {
		type: String,
		required: false,
		default: "",
	},
})

const contextMenu = ref<InstanceType<typeof ContextMenu> | null>(null)

const helpLabelStore = useHelpLabelStore()
const helpEditor = useHelpEditor()

const isAdminUser = computed(() => {
	try {
		return SessionInfo.currentUserIsAdmin() || SessionInfo.currentUser()?.role?.indexOf("Client Admin") !== -1
	} catch {
		return false
	}
})

const tooltipText = computed((): string | null => {
	if (!props.helpElementName) return null
	return helpLabelStore.getTooltip(props.helpElementName)
})

const labelHasHelpText = computed((): boolean => {
	if (!props.helpElementName) return false
	return helpLabelStore.hasHelpText(props.helpElementName)
})

const labelTooltipValue = computed((): string | null => {
	if (!props.helpElementName) return null
	if (tooltipText.value) return tooltipText.value
	return null
})

const sizeClass = computed(() => {
	switch (props.size) {
		case "h1":
			return "size-h1"
		case "h2":
			return "size-h2"
		case "h3":
			return "size-h3"
		case "h4":
			return "size-h4"
		default:
			return "size-normal"
	}
})

const contextMenuItems = computed(() => [
	{
		label: "Edit Tooltip",
		icon: "pi pi-pencil",
		command: () => {
			helpEditor.openTooltipEditor(props.helpElementName, props.label)
		},
	},
	{
		label: "Edit Help Text",
		icon: "pi pi-file-edit",
		command: () => {
			helpEditor.openHelpTextEditor(props.helpElementName, props.label)
		},
	},
])

function onPencilClick(event: MouseEvent): void {
	contextMenu.value?.show(event)
}
</script>

<style scoped>
.label-container {
	display: inline-flex;
	align-items: center;
	gap: 0.3em;
	position: relative;
}

.label-container label {
	cursor: default;
	margin-bottom: 0;
}

.pencil-icon {
	opacity: 0;
	transition: opacity 0.15s ease;
	color: #6b7280;
	font-size: 0.75em;
	cursor: pointer;
}

.pencil-icon:hover {
	color: #374151;
}

.label-container label:hover .pencil-icon,
.label-container .flex:hover .pencil-icon {
	opacity: 1;
}

.info-icon {
	color: #3b82f6;
	cursor: pointer;
	font-size: 0.9em;
	line-height: 1;
}

.info-icon:hover {
	color: #1d4ed8;
}

/* Size variants */
span.size-h1 > label > div {
	font-size: 1.9rem;
	font-weight: 700 !important;
}

.size-h2 label {
	font-size: 1.5rem;
	font-weight: 600;
}

.size-h3 label {
	font-size: 1.25rem;
	font-weight: 600;
}

.size-h4 label {
	font-size: 1.1rem;
	font-weight: 500;
}

.size-normal label {
	font-size: inherit;
	font-weight: inherit;
}
</style>
